import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DisableInput, DisregardEventResponse } from '@/app/models/disregardEvents';
import { useDiseblaAllEventsMutation, useListAllEventsQuery } from '@/app/services/disregardEventsApi';
import { useSearchAllPersonQuery } from '@/app/services/personApi';
import DrawerButtons from '@/components/common/DrawerButtons';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import TextButton from '@/components/common/TextButton';
import { Box, Typography, useTheme } from '@mui/material';
import { toast } from 'react-toastify';
import NewDisregardEventsTable from './NewDisregardEventsTable';
import { Collaborator } from '@/app/models/collaborator';

interface NewConsiderCollaboratorProps {
  setDrawerOpen: (open: boolean) => void;
}

export default function NewDisregardEvents(props: NewConsiderCollaboratorProps) {
  const theme = useTheme();
  const { t } = useTranslation(['disregardEvents', 'common']);

  const { setDrawerOpen } = props;

  const [selectedCollaborators, setSelectedCollaborators] = useState<DisregardEventResponse[]>([]);

  const [event, setEvent] = useState<DisregardEventResponse | null>(null);
  const [eventSearch, setEventSearch] = useState('');
  const { data: events, isFetching: isLoadingevents } = useListAllEventsQuery(
    {
      searchTerm: eventSearch,
      bringExcluded: false,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    { skip: eventSearch.length < 1 },
  );
  const handleAddCollaborator = () => {
    if (event) {
      setSelectedCollaborators((prevValue) => [...prevValue, event]);
      setEvent(null);
      setEventSearch('');
    }
  };

  const handleRemove = (removedCollaborator: string) => {
    setSelectedCollaborators((prevValue) => prevValue.filter((col) => col.code !== removedCollaborator));
  };

  const [disableAll] = useDiseblaAllEventsMutation();

  const handleSubmit = async () => {
    if (!selectedCollaborators || selectedCollaborators.length < 1) return;
    const requestBody: DisableInput = {
      codes: selectedCollaborators.map((i) => i.code),
    };
    await toast.promise(
      disableAll(requestBody),
      {
        pending: t('new.value.sent') as string,
        success: t('new.value.added') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
    setDrawerOpen(false);
  };

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const alreadyIncluded = (value: DisregardEventResponse) => {
    const filteredCollaborators = selectedCollaborators.filter((item) => item.code === value.code);
    return filteredCollaborators.length >= 1;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('disregard.event.title')} subtitle={t('disregard.event.subtitle') as string} />

        <Box sx={{ mt: 4 }}>
          <Typography component="p" variant="fieldHeader" sx={{ mt: 2 }}>
            {t('event')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <GenericAutoComplete
            value={event}
            onChange={(value) => {
              setEvent(value);
            }}
            equalityTest={(option, value) => option?.name === value?.name}
            getOptionLabel={(option) => `${option?.code} - ${option?.name}`}
            onSearch={(value) => {
              setEventSearch(value);
            }}
            options={eventSearch !== '' && events?.content ? events?.content : []}
            placeholder={t('search.event') || ''}
            loading={isLoadingevents}
            sx={{ width: theme.spacing(50) }}
          />

          <TextButton
            text={`+ ${t('remove')}`}
            disabled={!event || alreadyIncluded(event)}
            onClick={handleAddCollaborator}
          />
        </Box>

        <Box sx={{ mt: 4 }}>
          <NewDisregardEventsTable collaborators={selectedCollaborators} onRemove={handleRemove} />
        </Box>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons onSave={handleSubmit} onCancel={handleCancel} disabled={selectedCollaborators.length < 1} />
      </Box>
    </Box>
  );
}
